// Common props configuration used for export-code API.
// Based on original `src/common/UI/props.common.js` from the legacy repo,
// with `builderConstants` dependency removed (dataTitle in elementType simplified).

const flexBoxChild = {
  label: 'Flex Box Child Settings',
  type: 'FlexChildEditor',
  subProperties: ['order', 'flexGrow', 'flexShrink', 'flexBasis', 'alignSelf'],
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
}

const flexBox = {
  label: 'Flex Box ',
  type: 'FlexEditor',
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
  subProperties: ['flexDirection', 'flexWrap', 'justifyContent', 'alignItems', 'columnGap', 'rowGap'],
}

const apiIntegration = {
  label: 'APIIntegrations',
  type: 'APIController',
  group: 'APIIntegrations',
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web'],
}

const form = {
  label: 'Form Settings',
  type: 'FormController',
  group: 'FormSettings',
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web'],
}

const overflow = {
  type: 'Overflow',
  label: 'Overflow',
  group: 'Layout',
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
}

const textInput = {
  type: 'InputText',
}

const border = {
  label: 'Border',
  type: 'BorderEditor',
  subProperties: ['borderRadius', 'borderWidth', 'borderStyle', 'borderColor'],
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
}

const background = {
  label: 'Background',
  type: 'BackgroundEditor',
  subProperties: [
    'backgroundColor',
    'backgroundImage',
    'backgroundPosition',
    'backgroundSize',
    'backgroundRepeat',
    'backgroundAttachment',
    'backgroundClip',
    'backgroundOrigin',
    'background',
    'transform',
  ],
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
}

const margin = {
  type: 'MarginController',
  label: 'Position',
  group: 'Layout',
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
  subProperties: ['x', 'y', 'position'],
}

const children = {
  type: 'EditorHandler',
  info: 'Typography section',
  label: 'Editor',
  group: 'Typography',
  child: true,
  child_value: 'componentData.children',
}

const dimensions = {
  type: 'DimensionsController',
  label: 'Size',
  group: 'Layout',
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
  subProperties: ['w', 'h', 'minW', 'minH', 'maxW', 'maxH'],
}

const anchor = {
  type: 'AnchorController',
  label: 'Anchor Link',
  group: 'Link',
  wrap: true,
  wrap_value: 'componentData.anchor',
  html: `<a href=# class="link" target="_self"></a>`,
}

const padding = {
  type: 'Padding',
  label: 'Padding',
  group: 'Layout',
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
}

const boxShadow = {
  group: 'Style',
  type: 'BoxShadowEditor2',
  label: 'Shadow',
  subProperties: ['boxShadow'],
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
}

const effects = {
  group: 'Style',
  type: 'EffectsEditor',
  label: 'Effects',
  subProperties: ['opacity', 'transform', 'backdropFilter', 'filter'],
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
}

const fontSizeWeight = {
  type: 'FontSizeWeight',
  value: '',
  defaultValue: { web: '52px', mobile: '30px', tablet: '40px' },
  label: 'Text Size',
  subProperties: ['fontSize', 'fontWeight'],
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
}

const textAlign = {
  type: 'TextAlign',
  value: '',
  label: 'Horizontal Align',
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
  group: 'Typography',
}

const textSpacing = {
  type: 'FontSpacing',
  label: 'Spacing',
  group: 'Typography',
  subProperties: ['letterSpacing', 'lineHeight'],
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
}

const textTransform = {
  type: 'TextTransform',
  value: '',
  label: 'Text Transform',
  group: 'Typography',
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
}

const elementType = {
  type: 'Dropdown',
  label: 'Tagname',
  // Original used builderConstants.dataTitles["tagName"]; we don't need that for export,
  // so we just keep a simple string.
  dataTitle: 'tagName',
  options: [
    { label: 'div', value: 'div' },
    { label: 'span', value: 'span' },
    { label: 'section', value: 'section' },
    { label: 'article', value: 'article' },
    { label: 'address', value: 'address' },
  ],
  group: 'Tag',
  viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web'],
  element: true,
  element_value: 'componentData.elementType',
}

const CommonProps = {
  Body: {
    renderAsMixed: true,
    groupPriority: ['body', 'HiddenElements'],
    data: {
      hidden: {
        group: 'HiddenElements',
        type: 'AllHiddenComponents',
        label: 'Showing hidden components',
      },
    },
    style: {
      global: {
        border: {
          ...border,
          group: 'body',
        },
        background: {
          ...background,
          group: 'body',
        },
      },
    },
  },

  Button: {
    html: `<button data-component='Button'></button>`,
    renderAsMixed: true,
    groupPriority: ['Link', 'Layout', 'Typography', 'Style', 'FlexChild'],
    defaultOpenGroup: 'Style',
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      children: {
        ...children,
      },
      anchor: {
        ...anchor,
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
        },
        background: {
          ...background,
          group: 'Style',
        },
        boxShadow: {
          ...boxShadow,
        },
        border: { ...border, group: 'Style' },
        effects: {
          ...effects,
        },
        fontFamily: {
          type: 'FontFamily',
          value: '',
          group: 'Typography',
        },
        fontSizeWeight: {
          ...fontSizeWeight,
          group: 'Typography',
        },
        color: {
          type: 'Color',
          value: '',
          group: 'Typography',
        },
        textSpacing: {
          ...textSpacing,
        },
        textAlign: {
          ...textAlign,
          group: 'Typography',
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  Icon: {
    html: "<i data-component='Icon'></i>",
    renderAsMixed: true,
    groupPriority: ['Link', 'Layout', 'IconSettings', 'FlexChild'],
    defaultOpenGroup: 'IconSettings',
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      icon: {
        group: 'IconSettings',
        type: 'IconEditor',
        attr: 'class',
        attr_value: 'componentData.icon',
      },
      anchor: {
        ...anchor,
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
        },
        color: {
          type: 'Color',
          value: '',
          label: 'Color',
          group: 'IconSettings',
        },
        fontSizeWeight: {
          ...fontSizeWeight,
          subProperties: ['fontSize'],
          group: 'IconSettings',
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  LinkedComponentInstance: {
    renderAsMixed: true,
    defaultOpenGroup: 'Layout',
    groupPriority: ['Layout'],
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
    },
  },

  Section: {
    renderAsMixed: true,
    defaultOpenGroup: 'Style',
    groupPriority: ['Link', 'Columns', 'Layout', 'Flex', 'Style'],
    html: "<div data-component='DivComponent'> </div>",
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      columns: {
        group: 'Columns',
        viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web'],
        type: 'ColumnsGallery',
      },
      MultiList: {
        type: 'ColumnItemsEditor',
        group: 'Columns',
        viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        label: 'Customize columns',
        editType: 'Custom',
        validation: [
          (array, breakpoint) => {
            if (breakpoint === 'web') {
              return array.reduce((a, b) => a + (b['w'] || 0), 0) <= 12
            }
            return array.filter((a) => a.w > 12).length === 0
          },
          (array) => {
            let result = true
            array.forEach((a) => {
              if (a.w < 1) result = false
            })
            return result
          },
          (array) => array.length >= 1 && array.length <= 12,
        ],
      },
      elementType: {
        ...elementType,
      },
      anchor: {
        ...anchor,
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
        },
        overflow: {
          ...overflow,
        },
        flexBox: {
          ...flexBox,
          group: 'Flex',
        },
        background: {
          ...background,
          group: 'Style',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        boxShadow: {
          ...boxShadow,
        },
        border: {
          ...border,
          group: 'Style',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        effects: {
          ...effects,
        },
      },
    },
  },

  Form: {
    renderAsMixed: true,
    defaultOpenGroup: 'Style',
    groupPriority: ['Link', 'Layout', 'Flex', 'Style'],
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      elementType: {
        ...elementType,
      },
      apiIntegration: {
        ...apiIntegration,
      },
      form: {
        ...form,
      },
      anchor: {
        ...anchor,
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
        },
        overflow: {
          ...overflow,
        },
        flexBox: {
          ...flexBox,
          group: 'Flex',
        },
        background: {
          ...background,
          group: 'Style',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        boxShadow: {
          ...boxShadow,
        },
        border: {
          ...border,
          group: 'Style',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        effects: {
          ...effects,
        },
      },
    },
  },

  Columns: {
    html: "<div data-component='Columns'> </div>",
    renderAsMixed: true,
    groupPriority: ['Columns', 'ColumnLayout', 'Style'],
    defaultOpenGroup: 'Columns',
    structure: {
      w: {
        id: 'number',
        label: 'Column',
        name: 'w',
        default: true,
        value: 1,
      },
    },
    data: {
      margin: {
        ...margin,
        group: 'ColumnLayout',
      },
      dimensions: {
        ...dimensions,
        group: 'ColumnLayout',
      },
      columns: {
        group: 'Columns',
        viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web'],
        type: 'ColumnsGallery',
      },
      MultiList: {
        type: 'ColumnItemsEditor',
        group: 'Columns',
        viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        label: 'Customize columns',
        editType: 'Custom',
        validation: [
          (array, breakpoint) => {
            if (breakpoint === 'web') {
              return array.reduce((a, b) => a + (b['w'] || 0), 0) <= 12
            }
            return array.filter((a) => a.w > 12).length === 0
          },
          (array) => {
            let result = true
            array.forEach((a) => {
              if (a.w < 1) result = false
            })
            return result
          },
          (array) => array.length >= 1 && array.length <= 12,
        ],
      },
      horizontalAlign: {
        group: 'Columns',
        viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        type: 'HorizontalAlign',
        info: 'Choose one of the column structures below. You can customize it later. ',
        label: 'Horizontal Align',
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
          group: 'ColumnLayout',
        },
        background: {
          ...background,
          group: 'Style',
        },
        border: {
          ...border,
          group: 'Style',
        },
        boxShadow: {
          ...boxShadow,
        },
        effects: {
          ...effects,
        },
      },
    },
  },

  Image: {
    renderAsMixed: true,
    groupPriority: ['Link', 'Layout', 'Image', 'Style', 'FlexChild'],
    defaultOpenGroup: 'Image',
    html: "<span> <img data-component='Image'/> </span>",
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      anchor: {
        ...anchor,
      },
      image: {
        type: 'Image',
        label: 'Image',
        info: 'Replace Image',
        group: 'Image',
        child_attr: ['src', 'alt'],
        child_attr_value: ['src', 'componentData.properties.data-alt-text'],
      },
    },
    style: {
      global: {
        border: {
          ...border,
          group: 'Style',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        boxShadow: {
          ...boxShadow,
        },
        padding: {
          ...padding,
        },
        effects: {
          ...effects,
        },
        objectFit: {
          group: 'Image',
          type: 'ObjectFitSelector',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  HTMLCode: {
    html: `<div data-component='HTMLCode'></div>`,
    renderAsMixed: true,
    groupPriority: ['Link', 'Code', 'Layout', 'Flex', 'Style'],
    defaultOpenGroup: 'Code',
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      anchor: {
        ...anchor,
      },
      htmlCode: {
        type: 'CodeElementEditor',
        label: 'HTML Code',
        group: 'Code',
        inputPlaceholder: 'Paste your embed codes here...',
        keyName: 'htmlCode',
        child: true,
        child_value: 'componentData.htmlCode',
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
        },
        overflow: {
          ...overflow,
        },
        flexBox: {
          ...flexBox,
          group: 'Flex',
        },
        background: {
          ...background,
          group: 'Style',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        boxShadow: {
          ...boxShadow,
          group: 'Style',
        },
        border: {
          ...border,
          group: 'Style',
        },
        effects: {
          ...effects,
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  Svg: {
    renderAsMixed: true,
    groupPriority: ['Link', 'Image', 'Layout', 'Flex', 'Style'],
    defaultOpenGroup: 'Image',
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      anchor: {
        ...anchor,
      },
      svg: {
        type: 'TextareaWithLabel',
        label: 'SVG',
        svg: true,
        group: 'Image',
        keyName: 'svg',
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
        },
        overflow: {
          ...overflow,
        },
        flexBox: {
          ...flexBox,
          group: 'Flex',
        },
        background: {
          ...background,
          group: 'Style',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        boxShadow: {
          ...boxShadow,
          group: 'Style',
        },
        border: {
          ...border,
          group: 'Style',
        },
        effects: {
          ...effects,
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  Input: {
    renderAsMixed: true,
    groupPriority: ['Layout', 'Input', 'Typography', 'Style', 'Placeholder', 'FlexChild'],
    defaultOpenGroup: 'Input',
    html: "<input data-component='Input'></input>",
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      inputType: {
        type: 'Dropdown',
        label: 'Input Type',
        group: 'Input',
        attr: 'type',
        attr_value: 'componentData.inputType',
        options: [
          { label: 'text', value: 'text' },
          { label: 'number', value: 'number' },
          { label: 'email', value: 'email' },
          { label: 'checkbox', value: 'checkbox' },
          { label: 'date & time', value: 'datetime-local' },
          { label: 'date', value: 'date' },
          { label: 'time', value: 'time' },
          { label: 'password', value: 'password' },
        ],
      },
      placeholder: {
        type: 'InputText',
        inputProps: {
          label: 'Placeholder',
          placeholder: 'placeholder here',
          style: {
            width: '170px',
          },
        },
        group: 'Input',
        attr: 'placeholder',
        attr_value: 'componentData.placeholder',
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
        },
        overflow: {
          ...overflow,
        },
        fontFamily: {
          label: 'Typography',
          type: 'FontFamily',
          value: '',
          group: 'Typography',
        },
        fontSizeWeight: {
          ...fontSizeWeight,
          group: 'Typography',
        },
        color: {
          type: 'Color',
          group: 'Typography',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        textSpacing: {
          ...textSpacing,
        },
        textAlign: {
          ...textAlign,
          group: 'Typography',
        },
        background: {
          ...background,
          group: 'Style',
        },
        border: {
          ...border,
          group: 'Style',
        },
        boxShadow: {
          ...boxShadow,
          group: 'Style',
        },
        effects: {
          ...effects,
          group: 'Style',
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  Select: {
    renderAsMixed: true,
    groupPriority: ['Layout', 'DropdownSettings', 'Style', 'FlexChild'],
    defaultOpenGroup: 'DropdownSettings',
    html: "<select data-component='Dropdown'> </select>",
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      placeholder: {
        type: 'InputText',
        group: 'DropdownSettings',
        inputProps: {
          label: 'Placeholder',
          placeholder: 'placeholder for the dropdown field',
          style: {
            width: '180px',
          },
        },
      },
      MultiList: {
        group: 'DropdownSettings',
        label: 'Add/Edit options',
        type: 'MultiListForOptions',
        editType: 'Options',
        viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        moreOptions: false,
        multilist_html: "<option data-component='Option'></option>",
        multilist_attr: 'value',
        multilist_attr_value: 'value',
        multilist: 'componentData.Options',
        multilist_child: true,
        multilist_child_value: 'label',
        validation: [],
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
        },
        fontSizeWeight: {
          ...fontSizeWeight,
          group: 'Style',
        },
        fontFamily: {
          type: 'FontFamily',
          label: 'Font Family',
          value: '',
          group: 'Style',
        },
        color: {
          type: 'Color',
          label: 'Color',
          value: '',
          group: 'Style',
        },
        background: {
          ...background,
          group: 'Style',
        },
        border: {
          ...border,
          group: 'Style',
        },
        boxShadow: {
          ...boxShadow,
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  TextArea: {
    html: "<textarea data-component='TextArea'/>",
    renderAsMixed: true,
    groupPriority: ['Layout', 'TextArea', 'Typography', 'Style', 'Placeholder', 'FlexChild'],
    defaultOpenGroup: 'TextArea',
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      placeholder: {
        type: 'InputText',
        attr: 'placeholder',
        attr_value: 'componentData.placeholder',
        inputProps: {
          label: 'Placeholder',
          placeholder: 'placeholder here',
          style: {
            width: '170px',
          },
        },
        group: 'TextArea',
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
        },
        overflow: {
          ...overflow,
        },
        fontSizeWeight: {
          ...fontSizeWeight,
          group: 'Typography',
        },
        fontFamily: {
          type: 'FontFamily',
          label: 'Typography',
          value: '',
          group: 'Typography',
        },
        color: {
          type: 'Color',
          group: 'Typography',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        textSpacing: {
          ...textSpacing,
        },
        textAlign: {
          ...textAlign,
          group: 'Typography',
        },
        background: {
          ...background,
          group: 'Style',
        },
        border: {
          ...border,
          group: 'Style',
        },
        boxShadow: {
          ...boxShadow,
          group: 'Style',
        },
        effects: {
          ...effects,
          group: 'Style',
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  Text: {
    renderAsMixed: true,
    groupPriority: ['Link', 'Layout', 'Flex', 'Typography', 'Style', 'FlexChild'],
    defaultOpenGroup: 'Typography',
    html: "<span data-component='Text'></span>",
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      children: {
        ...children,
      },
      anchor: { ...anchor },
    },
    style: {
      global: {
        overflow: {
          ...overflow,
        },
        flexBox: {
          ...flexBox,
          group: 'Flex',
        },
        fontFamily: {
          type: 'FontFamily',
          group: 'Typography',
        },
        fontSizeWeight: {
          ...fontSizeWeight,
          group: 'Typography',
        },
        color: {
          type: 'Color',
          group: 'Typography',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        padding: {
          ...padding,
        },
        textAlign: {
          ...textAlign,
          group: 'Typography',
        },
        textTransform: {
          ...textTransform,
        },
        textSpacing: {
          ...textSpacing,
        },
        background: {
          ...background,
          group: 'Style',
        },
        boxShadow: {
          ...boxShadow,
          group: 'Style',
        },
        border: { ...border, group: 'Style' },
        effects: {
          ...effects,
          group: 'Style',
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  TextBlock: {
    renderAsMixed: true,
    groupPriority: ['Link', 'Layout', 'Typography', 'global', 'FlexChild'],
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      children: {
        ...children,
        visible: false,
      },
      anchor: {
        ...anchor,
      },
    },
    style: {
      global: {
        fontFamily: {
          type: 'FontFamily',
          value: '',
          group: 'Typography',
        },
        color: {
          type: 'Color',
          value: '',
          group: 'Typography',
        },
        fontSizeWeight: {
          ...fontSizeWeight,
          group: 'Typography',
        },
        padding: {
          ...padding,
        },
        textAlign: {
          ...textAlign,
          group: 'Typography',
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  Video: {
    renderAsMixed: true,
    groupPriority: ['Layout', 'Video', 'Style', 'FlexChild'],
    html: "<span> <video data-component='Video'/> </span>",
    defaultOpenGroup: 'Video',
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      video: {
        type: 'Video',
        label: 'Video',
        info: 'Replace Video',
        group: 'Video',
        child_attr: ['src', 'title', 'poster', 'controls', 'autoplay', 'loop', 'muted'],
        child_attr_value: [
          'componentData.videoEmbed.src',
          'componentData.videoEmbed.seoTitle',
          'componentData.videoEmbed.poster',
          'componentData.videoEmbed.controls',
          'componentData.videoEmbed.autoplay',
          'componentData.videoEmbed.loop',
          'componentData.videoEmbed.muted',
        ],
      },
    },
    style: {
      global: {
        effects: {
          ...effects,
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  VideoEmbedElement: {
    renderAsMixed: true,
    groupPriority: ['VideoEmbed', 'Layout', 'Style', 'FlexChild'],
    defaultOpenGroup: 'VideoEmbed',
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
    },
    style: {
      global: {
        padding: {
          ...padding,
        },
        overflow: {
          ...overflow,
        },
        background: {
          ...background,
          group: 'Style',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        boxShadow: {
          ...boxShadow,
        },
        border: {
          ...border,
          group: 'Style',
          viewOn: ['bp_1920', 'bp_1440', 'bp_1280', 'web', 'tablet', 'mobile'],
        },
        effects: {
          ...effects,
        },
      },
    },
  },

  Audio: {
    renderAsMixed: true,
    groupPriority: ['Layout', 'Audio', 'Style', 'FlexChild'],
    defaultOpenGroup: 'Audio',
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      src: {
        ...textInput,
        inputProps: {
          label: 'URL',
          placeholder:
            'e.g. https://soundcloud.com/paul-de-clerck/stephan-beneking-nocturnes-for-isabella-no-1-in-d-minor',
        },
        validation: [],
        label: 'Audio',
        group: 'Audio',
      },
      autoplay: {
        type: 'InputCheckbox',
        label: 'Autoplay',
        group: 'Audio',
      },
    },
    style: {
      global: {
        effects: {
          ...effects,
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },

  IFrame: {
    html: `<div data-component='IFrame'></div>`,
    renderAsMixed: true,
    groupPriority: ['Layout', 'IFrame', 'Style', 'FlexChild'],
    defaultOpenGroup: 'IFrame',
    data: {
      margin: {
        ...margin,
      },
      dimensions: {
        ...dimensions,
      },
      src: {
        type: 'IframeEditor',
        group: 'IFrame',
        child: true,
        child_value: 'componentData.src',
      },
    },
    style: {
      global: {
        effects: {
          ...effects,
        },
        flexBoxChild: {
          ...flexBoxChild,
          group: 'FlexChild',
        },
      },
    },
  },
}

export default CommonProps


